import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCheck, FaChevronDown, FaSearch, FaTimes } from 'react-icons/fa';

const getUniqueSelectedOptions = (selectedOptions = []) => {
  const seen = new Set();

  return (Array.isArray(selectedOptions) ? selectedOptions : []).filter((option) => {
    if (!option || seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
};

const AssignableUsersSelect = ({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = 'اختر مستخدمًا واحدًا أو أكثر',
  searchPlaceholder = 'ابحث بالاسم أو اسم المستخدم أو الصلاحية',
  noOptionsMessage = 'لا توجد نتائج مطابقة',
  noAvailableMessage = 'لا يوجد مستخدمون متاحون للتكليف',
  disabled = false,
  helperText = '',
  renderFooter = null
}) => {
  const controlRef = useRef(null);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [panelStyle, setPanelStyle] = useState(null);

  const selectedOptions = useMemo(() => getUniqueSelectedOptions(value), [value]);

  const selectedOptionIds = useMemo(
    () => new Set(selectedOptions.map((option) => option.value)),
    [selectedOptions]
  );

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const availableOptions = Array.isArray(options) ? options : [];

    if (!normalizedSearch) return availableOptions;

    return availableOptions.filter((option) => (
      (option.searchText || option.label || '').toLowerCase().includes(normalizedSearch)
    ));
  }, [options, search]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearch('');
  }, []);

  const updatePanelPosition = useCallback(() => {
    if (!controlRef.current) return;

    const rect = controlRef.current.getBoundingClientRect();
    const viewportPadding = 12;
    const desiredWidth = Math.min(rect.width, window.innerWidth - (viewportPadding * 2));
    const left = Math.max(
      viewportPadding,
      Math.min(rect.left, window.innerWidth - desiredWidth - viewportPadding)
    );
    const availableHeight = window.innerHeight - rect.bottom - 24;
    const maxHeight = Math.max(160, Math.min(320, availableHeight));

    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 8,
      left,
      width: desiredWidth,
      zIndex: 2500,
      '--tech-multiselect-options-max-height': `${maxHeight}px`
    });
  }, []);

  const openDropdown = useCallback(() => {
    if (disabled) return;
    updatePanelPosition();
    setIsOpen(true);
  }, [disabled, updatePanelPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [isOpen, updatePanelPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;

      if (
        controlRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }

      closeDropdown();
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeDropdown, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const timer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 20);

    return () => window.clearTimeout(timer);
  }, [isOpen]);

  const handleControlClick = useCallback(() => {
    if (isOpen) {
      closeDropdown();
      return;
    }

    openDropdown();
  }, [closeDropdown, isOpen, openDropdown]);

  const handleControlKeyDown = useCallback((event) => {
    if (disabled) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleControlClick();
    }
  }, [disabled, handleControlClick]);

  const handleSelectionChange = useCallback((nextSelectedOptions) => {
    if (typeof onChange === 'function') {
      onChange(getUniqueSelectedOptions(nextSelectedOptions));
    }
  }, [onChange]);

  const toggleOption = useCallback((option) => {
    const isSelected = selectedOptionIds.has(option.value);

    if (isSelected) {
      handleSelectionChange(
        selectedOptions.filter((selectedOption) => selectedOption.value !== option.value)
      );
      return;
    }

    handleSelectionChange([...selectedOptions, option]);
  }, [handleSelectionChange, selectedOptionIds, selectedOptions]);

  const removeOption = useCallback((optionValue) => {
    handleSelectionChange(
      selectedOptions.filter((selectedOption) => selectedOption.value !== optionValue)
    );
  }, [handleSelectionChange, selectedOptions]);

  const footerContent = useMemo(() => {
    if (typeof renderFooter === 'function') {
      return renderFooter({ closeDropdown, openDropdown });
    }

    return renderFooter;
  }, [closeDropdown, openDropdown, renderFooter]);

  return (
    <div className="tech-multiselect">
      {label && (
        <label className="tech-multiselect-label">{label}</label>
      )}

      <div
        ref={controlRef}
        className={`tech-multiselect-control ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        onClick={handleControlClick}
        onKeyDown={handleControlKeyDown}
      >
        <div className="tech-multiselect-value">
          {selectedOptions.length > 0 ? (
            selectedOptions.map((option) => (
              <span key={option.value} className="tech-multiselect-chip">
                <span className="tech-multiselect-chip-text">{option.chipLabel || option.label}</span>
                {!disabled && (
                  <button
                    type="button"
                    className="tech-multiselect-chip-remove"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeOption(option.value);
                    }}
                    aria-label={`Remove ${option.label}`}
                  >
                    <FaTimes />
                  </button>
                )}
              </span>
            ))
          ) : (
            <span className="tech-multiselect-placeholder">{placeholder}</span>
          )}
        </div>

        <div className="tech-multiselect-trigger">
          {selectedOptions.length > 0 && (
            <span className="tech-multiselect-count">{selectedOptions.length}</span>
          )}
          <FaChevronDown className={`tech-multiselect-chevron ${isOpen ? 'is-open' : ''}`} />
        </div>
      </div>

      {helperText && (
        <div className="tech-multiselect-helper">{helperText}</div>
      )}

      {isOpen && panelStyle && typeof document !== 'undefined' && createPortal(
        <div ref={panelRef} className="tech-multiselect-panel" style={panelStyle}>
          <div className="tech-multiselect-search">
            <FaSearch className="tech-multiselect-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              className="tech-multiselect-search-input"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onClick={(event) => event.stopPropagation()}
            />
          </div>

          <div className="tech-multiselect-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = selectedOptionIds.has(option.value);

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`tech-multiselect-option ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => toggleOption(option)}
                  >
                    <span className={`tech-multiselect-option-check ${isSelected ? 'is-selected' : ''}`}>
                      {isSelected && <FaCheck />}
                    </span>
                    <span className="tech-multiselect-option-body">
                      <span className="tech-multiselect-option-name">{option.label}</span>
                      {option.meta && (
                        <span className="tech-multiselect-option-meta">{option.meta}</span>
                      )}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="tech-multiselect-empty">
                {search.trim() ? noOptionsMessage : noAvailableMessage}
              </div>
            )}
          </div>

          {footerContent && (
            <div className="tech-multiselect-footer">{footerContent}</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default AssignableUsersSelect;
